<template>
  <admin-page-shell :title="$t('frontend_groovemail_admin_groove_users')">
    <server-table
      class="text-center groovemail-groove-users-table"
      ref="serverTable"
      :url="grooveUsersListingUrl"
      tableRef="usersTable"
      :slotData="grooveUsersSlotData"
      :tableColumns="grooveUsersTableColumns"
      :tableOptions="grooveUsersTableOptions"
      tableTitle="frontend_groovemail_admin_groove_users"
      bulkActionName="frontend_affiliates_bulk_actions"
      :bulkOptions="bulkActions"
      @loaded="hideLoader()"
      @loading="showLoader()"
      @pagination="pageChanged"
    >
      <template slot="top-left-content">
        <div class="gm-users-filters-wrapper">
          <treeselect
            v-model="userFilters"
            :multiple="true"
            :clearable="false"
            :closeOnSelect="true"
            :clearOnSelect="true"
            :options="filterOptions"
            :valueFormat="'object'"
            :placeholder="$t('frontend_filters')"
            @select="filterSelected"
            @deselect="filterRemoved"
          />
        </div>
      </template>
    </server-table>

    <div class="gm-admin-page__footer gm-users-goto">
      <gs-field :label="$t('frontend_groovemail_admin_groove_users_allow_goto_label')">
        <div class="gm-admin-tab__suffix">
          <text-input
            class="gm-users-goto__input"
            v-model="gotoPageNumber"
            value="gotoPageNumber"
            :placeholder="
              this.$t(
                'frontend_groovemail_admin_groove_users_allow_goto_specific_page_enter_page'
              )
            "
          />
          <gs-button
            variant="primary"
            @click="redirectToThisPage()"
            :disabled="gotoPageNumber <= 0 || isNaN(gotoPageNumber)"
          >
            {{ $t('frontend_groovemail_admin_groove_users_allow_goto_visit_label') }}
          </gs-button>
        </div>
      </gs-field>
    </div>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-action`"
      :name="`confirmation-action`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="confirmMessage"
      @confirm="confirmAction"
      @close="closeConfirmationActionModal"
    />

    <!-- Confirmation change status modal -->
    <confirm-modal
      :ref="`confirmation-change-status-allUsers`"
      :name="`confirmation-change-status-allUsers`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="confirmMessageForAllUsers"
      @confirm="changeMailingForAll"
      @close="closeChangeStatusAllUsersModal"
    />
  </admin-page-shell>
</template>

<script>
import axios from 'axios';
import Treeselect from '@riophae/vue-treeselect';
import { mapActions } from 'vuex';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import AdminPageShell from '../AdminPageShell.vue';
import { adminUrls } from '@/utils/urls.js';
import { getData, postData } from '@/utils/apiHelper.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import monthsMixin from '@/mixin/monthsMixin';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';

let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_admin_groove_users') };
  },

  mixins: [demoModeValuesMixin, monthsMixin],

  data() {
    return {
      grooveUsersListingUrl: adminUrls.grooveUsersUrl.usersListing,
      userFilters: [],
      grooveUsersTableColumns: [
        'name',
        'email',
        'username',
        'created_at',
        'level_display',
        'blocked',
        'can_mail',
        'can_import',
        'groovemail_smtp',
      ],
      grooveUsersTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        columnsDisplay: {
          name: 'min_mobile',
          email: 'min_mobile',
          username: 'min_desktop',
          created_at: 'min_desktop',
          level_display: 'min_desktop',
          blocked: 'min_mobile',
          can_mail: 'min_mobile',
          can_import: 'min_mobile',
          groovemail_smtp: 'min_mobile',
        },
        customFilters: [
          'mail_enable',
          'blocked_user',
          'groovemail_smtp',
          'can_import',
        ],
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'email', 'username', 'created_at', 'can_mail'],
        filterable: ['email'],
        headings: {
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          email: () => this.$t('frontend_resources_email'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          username: () => this.$t('frontend_resource_promo_username'),
          level_display: () =>
            this.$t('frontend_groovemail_admin_groove_users_level'),
          blocked: () => this.$t('frontend_groovemail_contacts_step3_blocked'),
          can_mail: () =>
            this.$t('frontend_groovemail_admin_groove_users_enable_mail'),
          can_import: () =>
            this.$t('frontend_groovemail_contacts_step6_import_leads'),
          groovemail_smtp: () =>
            this.$t('frontend_groovemail_admin_groove_users_smtp_access'),
        },
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
              self.hideLoader();
            });
        },
        responseAdapter({ data }) {
          if(self.demoMode) {
            const newData = data[0]?.data.map((obj) => ({
              ...obj,
              email: self.basicEmailText,
              name: self.basicText,
              username: self.basicEmailText,
            }));

            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: data[0].data,
            count: data[0].total,
          };
        },
      },
      gotoPageNumber: 1,

      confirmMessage: '',
      confirmMessageForAllUsers: '',
      confirmTarget: '',
      confirmUser: {},
      confirmEnble: null,
    };
  },

  components: {
    AdminPageShell,
    Treeselect,
    ServerTable,
  },

  created() {
    self = this;
  },

  watch: {
    // Used to refresh users table when demo mode changes
    demoMode() {
      this.$refs.serverTable.$refs.usersTable.refresh();
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     * Returns filters data for filtering users based on different parameters.
     * @return {array} filters
     */
    filterOptions() {
      return [
        {
          id: 1,
          type: 'mailing',
          label: this.$t(
            'frontend_groovemail_groove_users_mail_enabled_filter_option_enabled'
          ),
        },
        {
          id: 2,
          type: 'mailing',
          label: this.$t(
            'frontend_groovemail_groove_users_mail_enabled_filter_option_disabled'
          ),
        },
        {
          id: 3,
          type: 'block',
          label: this.$t(
            'frontend_groovemail_groove_users_users_blocked_filter_option_blocked'
          ),
        },
        {
          id: 4,
          type: 'block',
          label: this.$t(
            'frontend_groovemail_groove_users_users_blocked_filter_option_unblocked'
          ),
        },
        {
          id: 5,
          type: 'smtp',
          label: this.$t(
            'frontend_groovemail_groove_users_users_with_smtp_access_filter_option'
          ),
        },
        {
          id: 6,
          type: 'smtp',
          label: this.$t(
            'frontend_groovemail_groove_users_users_without_smtp_access_filter_option'
          ),
        },
        {
          id: 7,
          type: 'import',
          label: this.$t(
            'frontend_groovemail_admin_groove_users_users_with_import_access_filter_option'
          ),
        },
        {
          id: 8,
          type: 'import',
          label: this.$t(
            'frontend_groovemail_admin_groove_users_users_without_import_access_filter_option'
          ),
        },
      ];
    },

    /**	
     * Returns bulk actions options in dropdown
     * @return {array} data to enable or disable mailing for users
    */	
    bulkActions() {
      return [
        {
          id: 1,
          name: this.$t(
            'frontend_groovemail_admin_enable_disable_maling_setting_enable_mailing_text'
          ),
          callback: () => this.openChangeStatusAllUsersModal(1),
        },
        {
          id: 2,
          name: this.$t(
            'frontend_groovemail_admin_enable_disable_maling_setting_disable_mailing_text'
          ),
          callback: () => this.openChangeStatusAllUsersModal(0),
        },
      ];
    },

    /**	
     * Returns slot data for groove users table.
     * @return {array} details for slot columns for groove users table.
    */
    grooveUsersSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.name
              ? props.row.name
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.created_at
              ? `${new Date(props.row.created_at).getDate()} ${
                  this.months[new Date(props.row.created_at).getMonth()]
                }, ${new Date(props.row.created_at).getFullYear()}`
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'blocked',
          slotType: 'view',
          viewArray: [
            {
              id: 0,
              title: '',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.openConfirmationActionModal('chnageStatus', props.row);
              },
              buttonType: 'toggle',
              value: (props) => {
                return props.row.is_blocked;
              },
            },
          ],
        },
        {
          slotName: 'can_mail',
          slotType: 'view',
          viewArray: [
            {
              id: 0,
              title: '',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.openConfirmationActionModal('mailingStatus', props.row);
              },
              buttonType: 'toggle',
              value: (props) => {
                return props.row.can_mail;
              },
            },
          ],
        },
        {
          slotName: 'groovemail_smtp',
          slotType: 'view',
          viewArray: [
            {
              id: 0,
              title: '',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.openConfirmationActionModal('smtpAccess', props.row);
              },
              buttonType: 'toggle',
              value: (props) => {
                return props.row.groovemail_smtp;
              },
            },
          ],
        },
        {
          slotName: 'can_import',
          slotType: 'view',
          viewArray: [
            {
              id: 0,
              title: '',
              conditions: () => {
                return true;
              },
              callback: (props) => {
                return this.openConfirmationActionModal('importAccess', props.row);
              },
              buttonType: 'toggle',
              value: (props) => {
                return props.row.can_import;
              },
            },
          ],
        },
      ];
    },
  },

  methods: {
    ...mapActions('loader', ['LOADING']),

    // show loader
    showLoader() {
      this.LOADING(true);
    },

    // hide loader
    hideLoader (){
      this.LOADING(false);
    },



    // open delete blacklisted modal
    async openChangeStatusAllUsersModal(enable) {
      // set the Confirm id
      this.confirmEnble = enable;

      // set the Confirm message based on the target
      this.confirmMessageForAllUsers = enable
        ? this.$t('frontend_groovemail_admin_enable_disable_maling_setting_are_you_sure_on_for_all')
        : this.$t('frontend_groovemail_admin_enable_disable_maling_setting_are_you_sure_off_for_all');

      // open the modal
      this.$refs[`confirmation-change-status-allUsers`].open();
    },

    // close delete blacklisted modal
    closeChangeStatusAllUsersModal() {
      // reset the Confirm id
      this.confirmEnble = null;
    },

    // Triggers change in mailing status for all users.
    async changeMailingForAll() {
      try {
        await postData(adminUrls.grooveUsersUrl.changeMailingStatusForAll, {all_enable_disable: this.confirmEnble,});
        this.$refs.serverTable.$refs.usersTable.refresh();
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    // open confirmation action modal
    async openConfirmationActionModal(target, user) {
      this.confirmTarget = target;
      this.confirmUser = user;

      // set the Confirm message based on the target
      if (target === 'mailingStatus') {
        this.confirmMessage = user.can_mail
          ? this.$t('frontend_groovemail_admin_groove_users_sure_disable_mailing')
          : this.$t('frontend_groovemail_admin_groove_users_sure_enable_mailing');

      } else if (target === 'chnageStatus') {
        this.confirmMessage = user.is_blocked
        ? this.$t('frontend_groovemail_admin_groove_users_sure_unblock')
        : this.$t('frontend_groovemail_admin_groove_users_sure_block');

      } else if (target === 'smtpAccess') {
        this.confirmMessage = user.groovemail_smtp
        ? this.$t('frontend_groovemail_admin_groove_users_remove_smtp_access')
        : this.$t('frontend_groovemail_admin_groove_users_allow_smtp_access');

      } else if (target === 'importAccess') {
        this.confirmMessage = user.can_import
        ? this.$t('frontend_groovemail_admin_groove_users_sure_disable_importing_contacts')
        : this.$t('frontend_groovemail_admin_groove_users_sure_enable_importing_contacts');
      }

      // open the modal
      this.$refs[`confirmation-action`].open();
    },

    // close confirmation action modal
    closeConfirmationActionModal() {
      // reset the Confirm data
      this.confirmMessage = '';
      this.confirmTarget = '';
      this.confirmUser = {};
    },

    confirmAction() {
      if (this.confirmTarget === 'mailingStatus') {
        this.changeMailingStatus();
      } else if (this.confirmTarget === 'chnageStatus') {
        this.changeStatus();
      } else if (this.confirmTarget === 'smtpAccess') {
        this.changeSmtpAccess();
      } else if (this.confirmTarget === 'importAccess') {
        this.changeImportAccess();
      }
    },

    // Triggers change in mailing status for a particular user.
    async changeMailingStatus() {
      try {
        const response = await postData(adminUrls.grooveUsersUrl.changeMailingStatusForUser, {
          user_id: this.confirmUser.id,
          can_mail: 1 - this.confirmUser.can_mail,
        });
        await this.loadGrooveUsersListing(response);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    // Triggers change in block status for a particular user.
    async changeStatus() {
      try {
        const response = await postData(adminUrls.grooveUsersUrl.changeBlockStatusForUser, {
          user_id: this.confirmUser.id,
          block: 1 - this.confirmUser.is_blocked,
        });
        await this.loadGrooveUsersListing(response);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    // Triggers change in smtp access status for a particular user.
    async changeSmtpAccess() {
      try {
        const response = await postData(adminUrls.grooveUsersUrl.changeSMTPAccessForUser, {
          user_id: this.confirmUser.id,
          can_access_groovemail_smtp: 1 - this.confirmUser.groovemail_smtp,
        });
        await this.loadGrooveUsersListing(response);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    // Triggers change in import access status for a particular user.
    async changeImportAccess() {
      try {
        const response = await postData(adminUrls.grooveUsersUrl.changeImportAccessForUser, {
          user_id: this.confirmUser.id,
          can_import: 1 - this.confirmUser.can_import,
        });
        await this.loadGrooveUsersListing(response);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    /**
     * Calls API to fetch users data based on existing query.
     * @param {object} response response data
     */
    async loadGrooveUsersListing(response) {
      if (response.status === 200) {
        const userData = await getData(
          `${adminUrls.grooveUsersUrl.usersListing}?query=${this.$refs.serverTable.$refs.usersTable.filteredQuery}&limit=${this.grooveUsersTableOptions.perPage}&page=${this.$refs.serverTable.$refs.usersTable.Page}&mail_enable=${this.$refs.serverTable.$refs.usersTable.customQueries.mail_enable}&blocked_user=${this.$refs.serverTable.$refs.usersTable.customQueries.blocked_user}&groovemail_smtp=${this.$refs.serverTable.$refs.usersTable.customQueries.groovemail_smtp}&can_import=${this.$refs.serverTable.$refs.usersTable.customQueries.can_import}&ascending=0&byColumn=0`
        );

        this.$refs.serverTable.updateData(
          userData?.data[0]?.data,
          userData?.data[0]?.total
        );
      }
    },

    /**
     * Triggered when a page is changed via server table pagination event.
     * @param {number} pageNo page number selected
     */
    pageChanged(pageNo) {
      this.gotoPageNumber = pageNo;
    },
    
    // Used to navigate user to a specific page number on input
    async redirectToThisPage() {
      if (
        this.$refs.serverTable.$refs.usersTable.Page !== this.gotoPageNumber
      ) {
        this.$refs.serverTable.$refs.usersTable.setPage(this.gotoPageNumber);
      }
    },

    /**
     * Triggered when a filter selected.
     * @param {object} node filter data
     */
    async filterSelected(node) {
      const antiPresent = this.userFilters.find(
        (ele) => ele.type === node.type
      );
      if (antiPresent) {
        this.$nextTick(() => {
          const antiIndex = this.userFilters.findIndex(
            (ele) => ele.type === node.type
          );
          this.userFilters.splice(antiIndex, 1);
        });
      }
      if (node.type === 'mailing') {
        this.mailEnableFilterChanged(node.id % 2 === 1 ? 1 : 2);
      } else if (node.type === 'block') {
        this.blockedUserFilterChanged(node.id % 2 === 1 ? 1 : 2);
      } else if (node.type === 'smtp') {
        this.smtpAccessFilterChanged(node.id % 2 === 1 ? 1 : 2);
      } else if (node.type === 'import') {
        this.importAccessFilterChanged(node.id % 2 === 1 ? 1 : 2);
      }
    },

    /**
     * Triggered when a filter is removed.
     * @param {object} node filter data
     */
    async filterRemoved(node) {
      if (node.type === 'mailing') {
        this.mailEnableFilterChanged();
      } else if (node.type === 'block') {
        this.blockedUserFilterChanged();
      } else if (node.type === 'smtp') {
        this.smtpAccessFilterChanged();
      } else if (node.type === 'import') {
        this.importAccessFilterChanged();
      }
    },

    /**
     * Triggered when a mailing filter is selected.
     * @param {number} data (1 or 2)
     */
    mailEnableFilterChanged(data) {
      if (data) {
        this.$refs.serverTable.$refs.usersTable.customQueries.mail_enable =
          data;
      } else {
        this.$refs.serverTable.$refs.usersTable.customQueries.mail_enable = '';
      }
      this.$refs.serverTable.$refs.usersTable.refresh();
    },

    /**
     * Triggered when a block filter is selected.
     * @param {number} data (1 or 2)
     */
    blockedUserFilterChanged(data) {
      if (data) {
        this.$refs.serverTable.$refs.usersTable.customQueries.blocked_user =
          data;
      } else {
        this.$refs.serverTable.$refs.usersTable.customQueries.blocked_user = '';
      }
      this.$refs.serverTable.$refs.usersTable.refresh();
    },

    /**
     * Triggered when a smtp access filter is selected.
     * @param {number} data (1 or 2)
     */
    smtpAccessFilterChanged(data) {
      if (data) {
        this.$refs.serverTable.$refs.usersTable.customQueries.groovemail_smtp =
          data;
      } else {
        this.$refs.serverTable.$refs.usersTable.customQueries.groovemail_smtp =
          '';
      }
      this.$refs.serverTable.$refs.usersTable.refresh();
    },

    /**
     * Triggered when a import access filter is selected.
     * @param {number} data (1 or 2)
     */
    importAccessFilterChanged(data) {
      if (data) {
        this.$refs.serverTable.$refs.usersTable.customQueries.can_import = data;
      } else {
        this.$refs.serverTable.$refs.usersTable.customQueries.can_import = '';
      }
      this.$refs.serverTable.$refs.usersTable.refresh();
    },
  },
};
</script>


<style lang="scss">
.groovemail-groove-users-table #gd-server-table-view {
  .VueTables__table th:last-child span {
    display: inline-block;
  }
  .VueTables--server table {
    td:nth-child(1),
    th:nth-child(1) {
      width: 14%;
      word-break: break-word;
    }
    td:nth-child(2),
    th:nth-child(2),
    td:nth-child(3),
    th:nth-child(3) {
      width: 21%;
      word-break: break-word;
    }
    td:nth-child(4),
    th:nth-child(4) {
      width: 12.5%;
    }
    td:nth-child(5),
    th:nth-child(5),
    td:nth-child(6),
    th:nth-child(6),
    td:nth-child(7),
    th:nth-child(7) {
      width: 10.5%;
    }
  }
}

.gm-users-filters-wrapper {
  z-index: 10;
  .vue-treeselect {
    min-width: 260px;
    max-width: 420px;
  }
}

.gm-users-goto .gs-field {
  align-items: flex-end;
}

.gm-users-goto__input {
  width: 120px;
}
</style>
